import { useMemo } from 'react'
import keyBy from 'lodash/keyBy'
import { LanguageCode } from 'common/src/vtj/LanguageCode.enum'
import { getAllCountries } from 'todistukset-common/src/vtj/country.util'
import {
  CountryKoodistoCode,
  VtjCountryCodeAdditions,
} from 'todistukset-common/src/vtj/vtj-koodisto.util'

// Assignable to SingleSelectData
type CountrySelectData = {
  uniqueItemId: CountryKoodistoCode
  labelText: string
}

export function useCountrySelectData({
  lang,
  excludeCountries = [],
  includeSpecialCodes = [],
  includeFormerCountries = false,
}: {
  lang: LanguageCode
  excludeCountries?: CountryKoodistoCode[]
  includeSpecialCodes?: VtjCountryCodeAdditions[]
  includeFormerCountries?: boolean
}): {
  countrySelectList: CountrySelectData[]
  countrySelectIndex: Partial<Record<CountryKoodistoCode, CountrySelectData>>
} {
  return useMemo(() => {
    const excludeSpecialCodes: CountryKoodistoCode[] = Object.values(
      VtjCountryCodeAdditions
    ).filter((code) => !includeSpecialCodes.includes(code))
    const allExcludes = excludeSpecialCodes.concat(excludeCountries)
    const countrySelectList = getAllCountries(includeFormerCountries)
      .filter((item) => !allExcludes.includes(item.koodistoCode))
      .map((item) => {
        return {
          uniqueItemId: item.koodistoCode,
          labelText: item[lang as 'fi' | 'sv' | 'en'] ?? item.koodistoCode,
        }
      })
    countrySelectList.sort((a: CountrySelectData, b: CountrySelectData) =>
      a.labelText.localeCompare(b.labelText)
    )
    const countrySelectIndex = keyBy(countrySelectList, 'uniqueItemId')
    return { countrySelectList, countrySelectIndex }
  }, [lang, excludeCountries, includeSpecialCodes])
}

export function selectedSingleItem(
  code: CountryKoodistoCode | null | undefined,
  index: Partial<Record<CountryKoodistoCode, CountrySelectData>>
): CountrySelectData | undefined {
  return code && index[code] ? index[code] : undefined
}

export function selectedMultiItems(
  values: CountryKoodistoCode[] | null | undefined,
  index: Partial<Record<CountryKoodistoCode, CountrySelectData>>
): CountrySelectData[] {
  if (!Array.isArray(values)) {
    return []
  }
  return values
    .map((code) => (code && index[code] ? index[code] : undefined))
    .filter((csData): csData is NonNullable<typeof csData> => csData != null)
}
